import React, { useCallback } from 'react'

import { reportError } from 'src/lib/js/clientError'

import { t } from 'src/translations'
import classNames from 'classnames'

import {
  AlertModalSource,
  useAlertModalContext
} from 'shared/components/common/alert_modal/AlertModal'
import { usePopoverContext } from 'shared/components/common/popover/PopoverContext'
import { REQUEST_FAILURE_MESSAGE } from 'shared/js/constants'
import { ScreenWidth, useIsMobile } from 'shared/js/utils/WindowContext'

import { CART_POPOVER_CONTEXT_KEY } from 'public/components/default_template/online_ordering/cart/CartModal'
import QuantitySelector from 'public/components/default_template/online_ordering/item_modal/QuantitySelector'
import { useDisplayPageContext } from 'public/components/default_template/DisplayPageContext'
import { useCart } from 'public/components/online_ordering/CartContext'
import { useFulfillment } from 'public/components/online_ordering/FulfillmentContext'
import { useModifierContext } from 'public/components/online_ordering/ModifierContext'
import {
  ItemWithTimeBasedRule,
  useTimeBasedRules
} from 'public/components/online_ordering/TimeBasedRuleContext'
import { ItemSource } from 'public/components/default_template/online_ordering/cart/cartUtils'

type AddToCartSectionProps = {
  totalItemPrice: number
  setShowFooter: (showFooter: boolean) => void
  setModifiersInvalid: (showFooter: boolean) => void
  outOfStock?: boolean
}
export const VomAddToCartSection = ({
  totalItemPrice,
  setShowFooter,
  setModifiersInvalid,
  outOfStock
}: AddToCartSectionProps) => {
  const { displayedModifier, quantity, setQuantity, validate } =
    useModifierContext()
  const { addToCart, loadingCart } = useCart()
  const { fulfillmentData } = useFulfillment()
  const { openAlertModal } = useAlertModalContext()
  const isMobile = useIsMobile(ScreenWidth.SMALL)
  const context = usePopoverContext(CART_POPOVER_CONTEXT_KEY)
  const { channelGuid } = useDisplayPageContext()

  const {
    verifyItemIsFulfillable,
    verifyNoConflictingItemsInCart,
    timeBasedRulesMap
  } = useTimeBasedRules()
  const isDisabled = !quantity || loadingCart || outOfStock
  const fulfillmentType = fulfillmentData?.cartFulfillmentData.fulfillmentType
  const futureSchedules = fulfillmentData?.scheduleData.futureScheduleDates

  const onSubmit = useCallback(async () => {
    setModifiersInvalid(false)
    if (!displayedModifier) {
      return
    }

    if (!validate()) {
      setModifiersInvalid(true)
      setTimeout(() => {
        setModifiersInvalid(false)
      }, 5000)
      return
    }

    let errorType = null

    const timeBasedRules =
      timeBasedRulesMap[displayedModifier.modifier.itemGuid]
    try {
      const item = {
        itemGuid: displayedModifier.modifier.itemGuid,
        preorderRule: timeBasedRules?.preorderRule,
        leadTimeRule: timeBasedRules?.leadTimeRule,
        pickupWindowRule: timeBasedRules?.pickupWindowRule,
        name: displayedModifier.name,
        modifier: displayedModifier,
        quantity
      } as ItemWithTimeBasedRule
      if (!verifyNoConflictingItemsInCart(item)) {
        // opens ItemResolutionModal with the conflicting item
        return
      }

      const trackData = {
        quantity,
        unitOfMeasure: displayedModifier.unitOfMeasure,
        timeBasedRule: timeBasedRules?.leadTimeRule
          ? 'Minimum lead time'
          : timeBasedRules?.preorderRule
          ? 'Preorder'
          : timeBasedRules?.pickupWindowRule
          ? 'Pickup window'
          : null
      }

      errorType = await addToCart(
        displayedModifier,
        quantity,
        fulfillmentData?.cartFulfillmentData,
        trackData,
        channelGuid,
        ItemSource.Menu
      )
    } catch (error) {
      openAlertModal(
        REQUEST_FAILURE_MESSAGE,
        undefined,
        AlertModalSource.PRODUCT_DISPLAY
      )
      reportError('Error adding cart item.', error)
      errorType = 'CartModificationError'
    }
    if (errorType !== 'CartModificationError') {
      //only open the cart modal for desktop
      if (!isMobile) {
        context?.open()
      } else {
        setShowFooter(true)
      }
    }
  }, [
    displayedModifier,
    validate,
    timeBasedRulesMap,
    quantity,
    verifyNoConflictingItemsInCart,
    addToCart,
    fulfillmentData?.cartFulfillmentData,
    channelGuid,
    openAlertModal,
    isMobile,
    context,
    setShowFooter,
    setModifiersInvalid
  ])

  if (
    displayedModifier?.isRoot &&
    fulfillmentType &&
    !verifyItemIsFulfillable(
      displayedModifier.modifier.itemGuid,
      fulfillmentType,
      futureSchedules ?? []
    )
  ) {
    return (
      <button
        type='button'
        disabled
        className='modalButton addToCartButton centered'
      >
        {t('add-to-cart-section.item-not-currently-available')}
      </button>
    )
  }

  return (
    <div>
      {displayedModifier?.usesFractionalQuantity ? (
        <div className='fractionalQuantity modSection'>
          <div className='title'>{t('add-to-cart-section.quantity')}</div>
          <div className='quantity'>
            <input
              type='number'
              inputMode='decimal'
              disabled={loadingCart}
              name='fractionalQuantityInput'
              placeholder='0.00'
              defaultValue={quantity.toFixed(2)}
              value={quantity}
              onChange={(e) => setQuantity(parseFloat(e.target.value))}
            />
            <label htmlFor='fractionalQuantityInput'>
              {displayedModifier?.unitOfMeasure?.toLowerCase()}
            </label>
          </div>
          <i>
            {displayedModifier?.price}/
            {displayedModifier?.unitOfMeasure?.toLowerCase()}
          </i>
        </div>
      ) : null}
      <div className='addToCartRow'>
        <button
          onClick={onSubmit}
          type='button'
          disabled={isDisabled}
          className={classNames('modalButton addToCartButton centered', {
            outOfStock
          })}
          data-testid='pdp-add-to-cart-cta'
        >
          {outOfStock
            ? t('add-to-cart-section.out-of-stock')
            : t('add-to-cart-section.add-to-cart')}
        </button>
        {!displayedModifier?.usesFractionalQuantity ? (
          <div className='actionWrapper'>
            <QuantitySelector
              min={1}
              canIncrease={true}
              disabled={isDisabled}
              defaultValue={quantity}
              onQuantityChange={setQuantity}
              totalItemPrice={totalItemPrice}
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}
